import * as THREE from 'three';

export function createBlockInteractionSystem({
  scene,
  camera,
  controls,
  ui,
  world,
  player,
  itemDefs
}) {
  const raycaster = new THREE.Raycaster();

  const lookDir = new THREE.Vector3();
  const placementScratch = new THREE.Vector3();
  const rayOriginWorld = new THREE.Vector3();
  const rayDirWorld = new THREE.Vector3();
  const voxelPointScratch = new THREE.Vector3();
  const voxelNormalScratch = new THREE.Vector3();
  const aimCacheOriginScratch = new THREE.Vector3();
  const aimCacheDirScratch = new THREE.Vector3();
  const lastTargetAimOrigin = new THREE.Vector3();
  const lastTargetAimDir = new THREE.Vector3(0, 0, -1);
  const lastPreviewAimOrigin = new THREE.Vector3();
  const lastPreviewAimDir = new THREE.Vector3(0, 0, -1);

  const RAYCAST_CACHE_MAX_AGE_MS = 12;
  const TARGET_SAMPLE_MAX_AGE_MS = 33;
  const PREVIEW_SAMPLE_MAX_AGE_MS = 50;
  const AIM_CACHE_MAX_MOVE_SQ = 0.0036;
  const AIM_CACHE_MIN_DIR_DOT = 0.9994;
  const VOXEL_RAY_EPS = 0.0001;

  let lastPreviewSampleTime = 0;
  let cachedPlacementCandidate = null;
  let previewPlaceBlockId = null;
  let lastTargetSampleTime = 0;
  let cachedTargetPosition = null;

  let raycastCacheHit = null;
  let raycastCacheDist = 0;
  let raycastCacheAt = 0;
  let raycastCalls = 0;
  let raycastCacheHits = 0;
  let voxelRaycastCalls = 0;
  let dynamicRaycastCalls = 0;

  const previewMat = new THREE.MeshStandardMaterial({
    color: 0x7ad8ff,
    transparent: true,
    opacity: 0.28,
    emissive: 0x58c4ff,
    emissiveIntensity: 0.5,
    depthWrite: false
  });

  const previewMesh = new THREE.Mesh(new THREE.BoxGeometry(1.02, 1.02, 1.02), previewMat);
  previewMesh.visible = false;
  previewMesh.renderOrder = 999;
  scene.add(previewMesh);

  const previewWire = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.BoxGeometry(1.03, 1.03, 1.03)),
    new THREE.LineBasicMaterial({ color: 0xe7f9ff, transparent: true, opacity: 0.9, depthWrite: false })
  );
  previewWire.visible = false;
  previewWire.renderOrder = 1000;
  scene.add(previewWire);

  const targetFrame = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.BoxGeometry(1.012, 1.012, 1.012)),
    new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.98, depthWrite: false })
  );
  targetFrame.visible = false;
  targetFrame.renderOrder = 996;
  scene.add(targetFrame);

  function beginFrame(_frameToken = 0) {
    raycastCacheHit = null;
    raycastCacheDist = 0;
    raycastCacheAt = 0;
    raycastCalls = 0;
    raycastCacheHits = 0;
    voxelRaycastCalls = 0;
    dynamicRaycastCalls = 0;
  }

  function getEntryNormalFromDirection(dir, out = voxelNormalScratch) {
    const ax = Math.abs(dir.x);
    const ay = Math.abs(dir.y);
    const az = Math.abs(dir.z);
    if (ax >= ay && ax >= az) {
      out.set(dir.x >= 0 ? -1 : 1, 0, 0);
      return out;
    }
    if (ay >= az) {
      out.set(0, dir.y >= 0 ? -1 : 1, 0);
      return out;
    }
    out.set(0, 0, dir.z >= 0 ? -1 : 1);
    return out;
  }

  function getRenderableBlockAt(gx, gy, gz) {
    if (typeof world.getRenderableBlockAt === 'function') {
      return world.getRenderableBlockAt(gx, gy, gz);
    }
    if (typeof world.getBlockAt === 'function') {
      return world.getBlockAt(gx, gy, gz);
    }
    return null;
  }

  function captureCurrentAimSample(outOrigin, outDir) {
    camera.getWorldPosition(outOrigin);
    camera.getWorldDirection(outDir);
    if (outDir.lengthSq() > 1e-8) outDir.normalize();
  }

  function canReuseAimSample(lastSampleTime, maxAgeMs, cachedValue, lastOrigin, lastDir) {
    if (!cachedValue) return false;
    const now = performance.now();
    if ((now - lastSampleTime) >= maxAgeMs) return false;

    camera.getWorldPosition(aimCacheOriginScratch);
    if (aimCacheOriginScratch.distanceToSquared(lastOrigin) > AIM_CACHE_MAX_MOVE_SQ) return false;

    camera.getWorldDirection(aimCacheDirScratch);
    if (aimCacheDirScratch.lengthSq() <= 1e-8) return false;
    aimCacheDirScratch.normalize();
    return aimCacheDirScratch.dot(lastDir) >= AIM_CACHE_MIN_DIR_DOT;
  }

  function getVoxelTargetHit(maxDist = 6.0, origin = rayOriginWorld, direction = rayDirWorld) {
    voxelRaycastCalls += 1;
    const maxDistance = Math.max(0.5, Number(maxDist) || 6);
    const shiftedX = origin.x + 0.5;
    const shiftedY = origin.y + 0.5;
    const shiftedZ = origin.z + 0.5;

    let gx = Math.floor(shiftedX);
    let gy = Math.floor(shiftedY);
    let gz = Math.floor(shiftedZ);

    const stepX = direction.x > 0 ? 1 : (direction.x < 0 ? -1 : 0);
    const stepY = direction.y > 0 ? 1 : (direction.y < 0 ? -1 : 0);
    const stepZ = direction.z > 0 ? 1 : (direction.z < 0 ? -1 : 0);

    const tDeltaX = stepX === 0 ? Number.POSITIVE_INFINITY : (1 / Math.abs(direction.x));
    const tDeltaY = stepY === 0 ? Number.POSITIVE_INFINITY : (1 / Math.abs(direction.y));
    const tDeltaZ = stepZ === 0 ? Number.POSITIVE_INFINITY : (1 / Math.abs(direction.z));

    let tMaxX = stepX > 0
      ? ((gx + 1) - shiftedX) / direction.x
      : (stepX < 0 ? (shiftedX - gx) / -direction.x : Number.POSITIVE_INFINITY);
    let tMaxY = stepY > 0
      ? ((gy + 1) - shiftedY) / direction.y
      : (stepY < 0 ? (shiftedY - gy) / -direction.y : Number.POSITIVE_INFINITY);
    let tMaxZ = stepZ > 0
      ? ((gz + 1) - shiftedZ) / direction.z
      : (stepZ < 0 ? (shiftedZ - gz) / -direction.z : Number.POSITIVE_INFINITY);

    let travel = 0;
    const entryNormal = getEntryNormalFromDirection(direction);

    while (travel <= maxDistance + VOXEL_RAY_EPS) {
      const block = getRenderableBlockAt(gx, gy, gz);
      if (block && block.userData?.raycast !== false) {
        const hitDistance = Math.max(0, travel);
        voxelPointScratch.copy(origin).addScaledVector(direction, Math.min(maxDistance, hitDistance));
        return {
          distance: hitDistance,
          point: voxelPointScratch.clone(),
          face: { normal: entryNormal.clone() },
          object: block
        };
      }

      if (tMaxX <= tMaxY && tMaxX <= tMaxZ) {
        gx += stepX;
        travel = tMaxX;
        tMaxX += tDeltaX;
        entryNormal.set(-stepX, 0, 0);
      } else if (tMaxY <= tMaxZ) {
        gy += stepY;
        travel = tMaxY;
        tMaxY += tDeltaY;
        entryNormal.set(0, -stepY, 0);
      } else {
        gz += stepZ;
        travel = tMaxZ;
        tMaxZ += tDeltaZ;
        entryNormal.set(0, 0, -stepZ);
      }
    }
    return null;
  }

  function getDynamicTargetHit(maxDist = 6.0, origin = rayOriginWorld, direction = rayDirWorld) {
    const safeDist = Math.max(VOXEL_RAY_EPS, Number(maxDist) || 0);
    if (safeDist <= VOXEL_RAY_EPS) return null;

    const raycastTargets = typeof world.getDynamicRaycastObjectsAround === 'function'
      ? world.getDynamicRaycastObjectsAround(origin, safeDist)
      : [];
    if (!Array.isArray(raycastTargets) || raycastTargets.length <= 0) return null;

    dynamicRaycastCalls += 1;
    raycaster.far = safeDist;
    raycaster.set(origin, direction);
    const intersects = raycaster.intersectObjects(raycastTargets, false);

    for (const hit of intersects) {
      if (hit.distance > safeDist) break;
      const resolvedObject = typeof world.resolveRaycastHit === 'function'
        ? world.resolveRaycastHit(hit)
        : hit.object;
      if (!resolvedObject) continue;
      return {
        ...hit,
        object: resolvedObject
      };
    }

    return null;
  }

  function getTargetHit(maxDist = 6.0, opts = {}) {
    const force = !!opts.force;
    const now = performance.now();
    const safeDist = Math.max(0.5, maxDist);
    const canUseCache = !force
      && raycastCacheAt > 0
      && Math.abs(raycastCacheDist - safeDist) < 0.001
      && (now - raycastCacheAt) <= RAYCAST_CACHE_MAX_AGE_MS;

    if (canUseCache) {
      raycastCacheHits += 1;
      return raycastCacheHit;
    }

    raycastCalls += 1;
    captureCurrentAimSample(rayOriginWorld, rayDirWorld);
    if (rayDirWorld.lengthSq() <= 1e-8) {
      raycastCacheHit = null;
      raycastCacheDist = safeDist;
      raycastCacheAt = now;
      return null;
    }

    const voxelHit = getVoxelTargetHit(safeDist, rayOriginWorld, rayDirWorld);
    const dynamicSearchDist = voxelHit
      ? Math.min(safeDist, voxelHit.distance + VOXEL_RAY_EPS)
      : safeDist;
    const dynamicHit = getDynamicTargetHit(dynamicSearchDist, rayOriginWorld, rayDirWorld);
    const resolvedHit = dynamicHit || voxelHit || null;

    raycastCacheHit = resolvedHit;
    raycastCacheDist = safeDist;
    raycastCacheAt = now;
    return resolvedHit;
  }

  function getActivePlaceBlockId() {
    const itemId = ui.getActiveItemId();
    if (!itemId || ui.getActiveItemCount() <= 0) return null;

    const def = itemDefs[itemId];
    if (!def || def.placeBlockId === undefined) return null;
    return def.placeBlockId;
  }

  function getPlacementCandidate(hit) {
    if (hit) {
      placementScratch.copy(hit.object.position).add(hit.face.normal);
      return {
        x: Math.round(placementScratch.x),
        y: Math.round(placementScratch.y),
        z: Math.round(placementScratch.z)
      };
    }

    camera.getWorldDirection(lookDir);
    if (lookDir.y < -0.5) {
      const pos = player.getPosition();
      return {
        x: Math.round(pos.x),
        y: Math.floor(player.getFeetY() - 0.5),
        z: Math.round(pos.z)
      };
    }

    return null;
  }

  function canPlaceAtCandidate(candidate, placeBlockId) {
    if (!candidate) return false;
    if (!world.canPlaceAt(candidate.x, candidate.y, candidate.z)) return false;
    if (placeBlockId === 12 && !world.canSupportTorchAt(candidate.x, candidate.y, candidate.z)) return false;

    return !player.blockIntersectsPlayer(candidate.x, candidate.y, candidate.z, player.getPosition(), player.getHeight());
  }

  function clearPreview() {
    cachedPlacementCandidate = null;
    previewPlaceBlockId = null;
    cachedTargetPosition = null;
    lastPreviewSampleTime = 0;
    lastTargetSampleTime = 0;
    previewMesh.visible = false;
    previewWire.visible = false;
    targetFrame.visible = false;
  }

  function refreshTargetHighlight(force = false) {
    targetFrame.visible = false;

    if (!controls.isLocked || ui.isCraftOpen()) return null;

    if (!force && canReuseAimSample(
      lastTargetSampleTime,
      TARGET_SAMPLE_MAX_AGE_MS,
      cachedTargetPosition,
      lastTargetAimOrigin,
      lastTargetAimDir
    )) {
      targetFrame.position.set(cachedTargetPosition.x, cachedTargetPosition.y, cachedTargetPosition.z);
      targetFrame.visible = true;
      return null;
    }

    lastTargetSampleTime = performance.now();
    captureCurrentAimSample(lastTargetAimOrigin, lastTargetAimDir);
    const hit = getTargetHit(6.0, { force });
    if (!hit?.object?.position) {
      cachedTargetPosition = null;
      return null;
    }

    cachedTargetPosition = {
      x: hit.object.position.x,
      y: hit.object.position.y,
      z: hit.object.position.z
    };
    targetFrame.position.set(cachedTargetPosition.x, cachedTargetPosition.y, cachedTargetPosition.z);
    targetFrame.visible = true;
    return hit;
  }

  function refreshPlacementPreview(force = false) {
    previewMesh.visible = false;
    previewWire.visible = false;

    if (!controls.isLocked || ui.isCraftOpen()) return null;

    const placeBlockId = getActivePlaceBlockId();
    if (placeBlockId === null) return null;

    if (!force
      && previewPlaceBlockId === placeBlockId
      && canReuseAimSample(
        lastPreviewSampleTime,
        PREVIEW_SAMPLE_MAX_AGE_MS,
        cachedPlacementCandidate,
        lastPreviewAimOrigin,
        lastPreviewAimDir
      )) {
      previewMesh.position.set(cachedPlacementCandidate.x, cachedPlacementCandidate.y, cachedPlacementCandidate.z);
      previewWire.position.copy(previewMesh.position);
      previewMesh.visible = true;
      previewWire.visible = true;
      return cachedPlacementCandidate;
    }

    lastPreviewSampleTime = performance.now();
    captureCurrentAimSample(lastPreviewAimOrigin, lastPreviewAimDir);
    const hit = getTargetHit(6.0, { force });
    let candidate = getPlacementCandidate(hit);

    if (!canPlaceAtCandidate(candidate, placeBlockId)) {
      // Fallback: when ray hits an invalid face while pillar-jumping, try under-foot placement.
      const fallbackCandidate = getPlacementCandidate(null);
      if (canPlaceAtCandidate(fallbackCandidate, placeBlockId)) {
        candidate = fallbackCandidate;
      } else {
        cachedPlacementCandidate = null;
        previewPlaceBlockId = null;
        return null;
      }
    }

    cachedPlacementCandidate = candidate;
    previewPlaceBlockId = placeBlockId;

    previewMesh.position.set(candidate.x, candidate.y, candidate.z);
    previewWire.position.copy(previewMesh.position);
    previewMesh.visible = true;
    previewWire.visible = true;

    return candidate;
  }

  function getPerfSnapshot() {
    return {
      raycastCalls,
      raycastCacheHits,
      voxelRaycastCalls,
      dynamicRaycastCalls
    };
  }

  return {
    beginFrame,
    clearPreview,
    getTargetHit,
    getActivePlaceBlockId,
    refreshPlacementPreview,
    refreshTargetHighlight,
    getPerfSnapshot
  };
}
